(() => {
  if (window.__HARPAH_CART_DRAWER_V11__) return;
  window.__HARPAH_CART_DRAWER_V11__ = true;

  const ROOT = '[data-hcd3]';
  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const drawer = () => document.querySelector(ROOT);
  const sheet = () => document.querySelector('[data-hcd3-options]');

  const sectionId = () => {
    const el = drawer();
    return el?.dataset.sectionId || window.HARPAH_CART_DRAWER_V3?.sectionId || 'h-cart-drawer-v3';
  };

  const setStatus = (message = '') => {
    const node = drawer()?.querySelector('[data-hcd3-status]');
    if (node) node.textContent = message;
  };

  const lock = () => document.documentElement.classList.add('hcd3-lock');
  const unlock = () => document.documentElement.classList.remove('hcd3-lock');

  const open = () => {
    const el = drawer();
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    lock();
    requestAnimationFrame(() => el.querySelector('.hcd3__panel')?.focus());
  };

  const close = () => {
    const el = drawer();
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    unlock();
  };

  const getCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load shopping bag.');
    return response.json();
  };

  const fetchFreshDrawer = async ({ keepOpen = true } = {}) => {
    const current = drawer();
    if (!current) throw new Error('Cart drawer is not mounted.');

    const wasOpen = current.classList.contains('is-open');
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId());

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) throw new Error('Unable to refresh shopping bag.');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(ROOT);

    if (!next) throw new Error('Cart drawer markup was not returned.');

    current.replaceWith(next);

    if (keepOpen || wasOpen) {
      next.classList.add('is-open');
      next.setAttribute('aria-hidden', 'false');
      lock();
    }

    return next;
  };

  const refreshAndOpen = async () => {
    try {
      await fetchFreshDrawer({ keepOpen: true });
      open();

      if (isMobileGiftMode()) {
        const added = await ensureMobileUnlockedGifts();

        // ensureMobileUnlockedGifts refreshes the drawer after adding.
        // Re-open once more so the newly-added gift lines remain visible.
        if (added) open();
      }
    } catch (error) {
      open();
      setStatus(error.message || 'Unable to refresh shopping bag.');
    }
  };

  const changeLine = async (key, quantity) => {
    setStatus('Updating your bag…');

    const response = await fetch(`${rootUrl()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: Math.max(0, Number(quantity) || 0)
      })
    });

    const result = await response.json();
    if (!response.ok) throw result;

    await fetchFreshDrawer({ keepOpen: true });

    if (isMobileGiftMode()) {
      await ensureMobileUnlockedGifts();
      open();
    }

    setStatus(Number(quantity) === 0 ? 'Item removed.' : 'Bag updated.');

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: result
    }));
  };

  let autoGiftSyncRunning = false;

  const getMissingUnlockedGifts = () => {
    const el = drawer();
    if (!el) return [];

    const gifts = [];

    [1, 2, 3].forEach((tier) => {
      const variantId = el.getAttribute(`data-gift-${tier}-variant-id`);
      const unlocked = el.getAttribute(`data-gift-${tier}-unlocked`) === 'true';
      const inCart = el.getAttribute(`data-gift-${tier}-in-cart`) === 'true';
      const label =
        el.getAttribute(`data-gift-${tier}-label`) ||
        `Gift ${tier}`;

      if (variantId && variantId !== '' && unlocked && !inCart) {
        gifts.push({
          id: Number(variantId),
          quantity: 1,
          properties: {
            _harpah_free_gift: 'true',
            _harpah_gift_tier: String(tier),
            _harpah_gift_label: label
          }
        });
      }
    });

    return gifts.filter((gift) => Number.isFinite(gift.id) && gift.id > 0);
  };

  const isMobileGiftMode = () => window.matchMedia('(max-width: 749px)').matches;

  const ensureUnlockedGifts = async ({ mobileOnly = false } = {}) => {
    if (mobileOnly && !isMobileGiftMode()) return false;
    if (autoGiftSyncRunning) return false;

    const missing = getMissingUnlockedGifts();
    if (!missing.length) return false;

    autoGiftSyncRunning = true;

    try {
      setStatus(
        missing.length > 1
          ? 'Adding your unlocked gifts…'
          : 'Adding your unlocked gift…'
      );

      const response = await fetch(`${rootUrl()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: missing })
      });

      const result = await response.json();
      if (!response.ok) throw result;

      await fetchFreshDrawer({ keepOpen: true });

      setStatus(
        missing.length > 1
          ? 'Your unlocked gifts were added.'
          : 'Your unlocked gift was added.'
      );

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: result
      }));

      return true;
    } finally {
      autoGiftSyncRunning = false;
    }
  };

  const ensureMobileUnlockedGifts = async () => {
    return ensureUnlockedGifts({ mobileOnly: true });
  };

  const addVariant = async (variantId, properties = {}) => {
    if (!variantId) throw new Error('Please choose an option.');

    setStatus('Adding to your bag…');

    const response = await fetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: Number(variantId),
          quantity: 1,
          properties
        }]
      })
    });

    const result = await response.json();
    if (!response.ok) throw result;

    await fetchFreshDrawer({ keepOpen: true });

    if (isMobileGiftMode()) {
      await ensureMobileUnlockedGifts();
    }

    open();
    setStatus('Added to your bag.');

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: result
    }));

    return result;
  };

  const isCartLink = (anchor) => {
    if (!anchor) return false;
    try {
      const url = new URL(anchor.href, window.location.origin);
      const pathname = url.pathname.replace(/\/+$/, '');
      return pathname === '/cart' || pathname.endsWith('/cart');
    } catch (_) {
      return false;
    }
  };

  const openOptions = (productId) => {
    const template = drawer()?.querySelector(
      `[data-hcd3-product-options="${CSS.escape(String(productId))}"]`
    );
    const modal = sheet();
    const content = modal?.querySelector('[data-hcd3-options-content]');

    if (!template || !modal || !content) return;

    content.innerHTML = template.innerHTML;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
  };

  const closeOptions = () => {
    const modal = sheet();
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
  };

  const addPendingSelectionsThenCheckout = async (checkout) => {
    if (isMobileGiftMode()) {
      try {
        await ensureMobileUnlockedGifts();
      } catch (error) {
        setStatus(
          error?.description ||
          error?.message ||
          'Unable to add your unlocked gift before checkout.'
        );
        return;
      }
    }

    const el = drawer();
    if (!el) {
      window.location.href = '/checkout';
      return;
    }

    const pending = Array.from(el.querySelectorAll('[data-hcd3-rec-card]'))
      .filter(card => card.dataset.selectedVariantId && card.dataset.addedToCart !== 'true');

    if (!pending.length) {
      window.location.href = checkout.href || '/checkout';
      return;
    }

    const items = pending.map(card => ({
      id: Number(card.dataset.selectedVariantId),
      quantity: 1,
      properties: { _offer_surface: 'cart_drawer_checkout' }
    }));

    checkout.classList.add('is-loading');

    try {
      const response = await fetch(`${rootUrl()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      });

      const result = await response.json();
      if (!response.ok) throw result;

      window.location.href = checkout.href || '/checkout';
    } catch (error) {
      checkout.classList.remove('is-loading');
      setStatus(error?.description || error?.message || 'Unable to add your selected item.');
    }
  };

  // Global delegated interactions. These survive every AJAX drawer replacement.
  document.addEventListener('click', async (event) => {
    const target = event.target;

    const closeButton = target.closest('[data-hcd3-close]');
    if (closeButton) {
      event.preventDefault();
      close();
      return;
    }

    const optionsClose = target.closest('[data-hcd3-options-close]');
    if (optionsClose) {
      event.preventDefault();
      closeOptions();
      return;
    }

    const qty = target.closest('[data-hcd3-qty]');
    if (qty && qty.closest(ROOT)) {
      event.preventDefault();

      if (qty.dataset.hcd3Busy === 'true') return;
      qty.dataset.hcd3Busy = 'true';

      try {
        await changeLine(qty.dataset.key, qty.dataset.quantity);
      } catch (error) {
        delete qty.dataset.hcd3Busy;
        setStatus(error?.description || error?.message || 'Unable to update your bag.');
      }
      return;
    }

    const gift = target.closest('[data-hcd3-add-gift]');
    if (gift && gift.closest(ROOT)) {
      event.preventDefault();
      try {
        gift.disabled = true;
        await addVariant(gift.dataset.variantId, {
          _harpah_free_gift: 'true',
          _harpah_gift_tier: gift.dataset.giftTier || '',
          _harpah_gift_label: gift.dataset.giftLabel || ''
        });
      } catch (error) {
        gift.disabled = false;
        setStatus(error?.description || error?.message || 'Unable to add your gift.');
      }
      return;
    }

    const size = target.closest('[data-hcd3-size-variant]');
    if (size && size.closest(ROOT)) {
      event.preventDefault();
      if (size.disabled || !size.dataset.hcd3SizeVariant) return;

      const card = size.closest('[data-hcd3-rec-card]');
      if (!card) return;

      card.querySelectorAll('[data-hcd3-size-variant]').forEach(button => {
        button.classList.toggle('is-selected', button === size);
      });

      card.dataset.selectedVariantId = size.dataset.hcd3SizeVariant;
      card.dataset.addedToCart = 'false';

      const note = card.querySelector('[data-hcd3-size-note]');
      if (note) {
        note.classList.remove('is-error');
        note.textContent = `Size ${size.textContent.trim()} selected`;
      }
      return;
    }

    const cardAdd = target.closest('[data-hcd3-card-add]');
    if (cardAdd && cardAdd.closest(ROOT)) {
      event.preventDefault();

      const card = cardAdd.closest('[data-hcd3-rec-card]');
      const variantId = card?.dataset.selectedVariantId;
      const note = card?.querySelector('[data-hcd3-size-note]');

      if (!variantId) {
        if (note) {
          note.classList.add('is-error');
          note.textContent = 'Please select a size';
        }
        return;
      }

      try {
        cardAdd.disabled = true;
        card.dataset.addedToCart = 'true';
        await addVariant(variantId, { _offer_surface: 'cart_drawer' });
      } catch (error) {
        cardAdd.disabled = false;
        if (card) card.dataset.addedToCart = 'false';
        setStatus(error?.description || error?.message || 'Unable to add this item.');
      }
      return;
    }

    const quickAdd = target.closest('[data-hcd3-quick-add]');
    if (quickAdd && quickAdd.closest(ROOT)) {
      event.preventDefault();
      try {
        quickAdd.disabled = true;
        await addVariant(quickAdd.dataset.hcd3QuickAdd, { _offer_surface: 'cart_drawer' });
      } catch (error) {
        quickAdd.disabled = false;
        setStatus(error?.description || error?.message || 'Unable to add this item.');
      }
      return;
    }

    const optionsOpen = target.closest('[data-hcd3-open-options]');
    if (optionsOpen && optionsOpen.closest(ROOT)) {
      event.preventDefault();
      openOptions(optionsOpen.dataset.hcd3OpenOptions);
      return;
    }

    const optionVariant = target.closest('[data-hcd3-option-variant]');
    if (optionVariant && optionVariant.closest('[data-hcd3-options]')) {
      event.preventDefault();
      try {
        optionVariant.disabled = true;
        await addVariant(optionVariant.dataset.hcd3OptionVariant, {
          _offer_surface: 'cart_drawer'
        });
        closeOptions();
      } catch (error) {
        optionVariant.disabled = false;
        setStatus(error?.description || error?.message || 'Unable to add this option.');
      }
      return;
    }

    const prev = target.closest('[data-hcd3-rec-prev]');
    const next = target.closest('[data-hcd3-rec-next]');
    if ((prev || next) && (prev || next).closest(ROOT)) {
      event.preventDefault();
      const section = (prev || next).closest('.hcd3__recs');
      const track = section?.querySelector('[data-hcd3-rec-track]');
      if (!track) return;
      const distance = track.clientWidth * 0.78 * (prev ? -1 : 1);
      track.scrollBy({ left: distance, behavior: 'smooth' });
      return;
    }

    const checkout = target.closest('[data-hcd3-checkout]');
    if (checkout && checkout.closest(ROOT)) {
      event.preventDefault();
      await addPendingSelectionsThenCheckout(checkout);
      return;
    }

    const cartAnchor = target.closest('a');
    const explicitCartTrigger = target.closest('[data-cart-drawer-open], [data-hcd3-open]');

    if (
      !target.closest(ROOT) &&
      (explicitCartTrigger || isCartLink(cartAnchor))
    ) {
      event.preventDefault();
      await refreshAndOpen();
      return;
    }

    // PDP V3 / Sticky Bar fallback:
    // Remember the drawer's already-rendered cart count synchronously.
    const addToBag = target.closest('[data-add-to-cart]');
    if (addToBag && !target.closest(ROOT)) {
      const before = Number(drawer()?.dataset.cartCount || 0);

      const check = async () => {
        try {
          const cart = await getCart();
          if (cart.item_count > before) {
            await refreshAndOpen();
            return true;
          }
        } catch (_) {}
        return false;
      };

      window.setTimeout(check, 450);
      window.setTimeout(check, 900);
      window.setTimeout(check, 1500);
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-hcd3-discount]');
    if (!form) return;

    event.preventDefault();
    const code = form.querySelector('input[name="discount"]')?.value?.trim();
    if (!code) return;

    sessionStorage.setItem('hcd3-reopen', '1');
    const redirect = `${window.location.pathname}${window.location.search}`;
    window.location.href =
      `${rootUrl()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`;
  });

  // PDP V3 v20 emits this only after cart/add.js succeeded.
  const onPdpAdded = () => refreshAndOpen();
  window.addEventListener('harpah:cart-added', onPdpAdded);
  document.addEventListener('harpah:cart-added', onPdpAdded);

  // Compatibility with earlier V3 builds.
  document.addEventListener('cart:open', refreshAndOpen);
  document.addEventListener('harpah:cart-open', refreshAndOpen);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (sheet()?.classList.contains('is-open')) {
      closeOptions();
    } else if (drawer()?.classList.contains('is-open')) {
      close();
    }
  });

  if (sessionStorage.getItem('hcd3-reopen') === '1') {
    sessionStorage.removeItem('hcd3-reopen');
    refreshAndOpen();
  }

  // Public bridge for any future HARPAH component.
  const syncMobileRewardsIfNeeded = () => {
    if (!isMobileGiftMode()) return;
    if (!drawer()?.classList.contains('is-open')) return;

    ensureMobileUnlockedGifts().catch((error) => {
      setStatus(
        error?.description ||
        error?.message ||
        'Unable to add your unlocked gift.'
      );
    });
  };

  window.addEventListener('pageshow', syncMobileRewardsIfNeeded);
  window.addEventListener('resize', syncMobileRewardsIfNeeded);

  window.addEventListener('DOMContentLoaded', () => {
    if (!isMobileGiftMode()) return;

    const el = drawer();
    if (!el) return;

    // If the drawer is already mounted/open from theme state, sync immediately.
    if (el.classList.contains('is-open')) {
      ensureMobileUnlockedGifts().catch(() => {});
    }
  });

  window.HARPAH_CART_DRAWER_V3_API = {
    open: refreshAndOpen,
    refresh: fetchFreshDrawer,
    close,
    addVariant,
    changeLine,
    syncMobileGifts: ensureMobileUnlockedGifts,
    getMissingUnlockedGifts
  };
})();
